/**
 * 「Demo 画布」——新建模板时的起步内容。
 *
 * 空模板对新手是最难的一步：一张白纸 + 一列区块按钮，用户不知道该先放什么、
 * 字段怎么绑、明细表长什么样。竞品的新手教程就是一份填好的示例文档，边看边改。
 * 这里照同样的思路，用当前表的真实字段名生成一份能直接打印的示例。
 *
 * 刻意用真字段而不是占位词：生成完立刻就能看到自己的数据，而不是一堆
 * 「{{字段1}}」让人再去一个个替换。
 */
import { genId } from '../contract/types';
import type { ContractSection, FieldMeta } from '../contract/types';

const LINK_TYPES = [18, 21];

function cell(content: string, extra: Record<string, unknown> = {}) {
  return {
    id: genId(),
    content,
    colSpan: 1,
    rowSpan: 1,
    align: 'left' as const,
    bold: false,
    shaded: false,
    merged: false,
    ...extra,
  };
}

/**
 * 按表字段拼一份示例。挑字段的规则很朴素：前几个非关联字段进信息网格，
 * 第一个关联字段（如果有）生成一张动态明细表。
 */
export function buildDemoSections(fieldMetas: FieldMeta[], tableName: string): ContractSection[] {
  const plain = fieldMetas.filter((f) => !LINK_TYPES.includes(f.type) && f.type !== 17);
  const link = fieldMetas.find((f) => LINK_TYPES.includes(f.type));
  const gridFields = plain.slice(0, 6);

  const sections: ContractSection[] = [
    {
      type: 'header',
      id: genId(),
      content: `${tableName || '单据'}示例`,
      align: 'center',
      fontSizePx: 22,
      bold: true,
    } as ContractSection,
    {
      type: 'text_block',
      id: genId(),
      content:
        '这是一份示例：双击任意文字可直接改，块之间悬停会出现「+ 在这里插入」，' +
        '拖左侧六点可调顺序。左边点字段就能插进来。改完点右上角「预览」看真实数据。',
      fontSizePx: 12,
    } as ContractSection,
    { type: 'divider', id: genId(), style: 'line' } as ContractSection,
  ];

  if (gridFields.length > 0) {
    sections.push({
      type: 'info_grid',
      id: genId(),
      columns: 2,
      items: gridFields.map((f) => ({
        id: genId(),
        label: f.name,
        fieldId: f.id,
        staticValue: '',
      })),
    } as ContractSection);
  }

  if (link) {
    // 明细表：表头 + 样板行 + 合计，样板行随子表自动加行
    const cols = 4;
    sections.push({ type: 'divider', id: genId(), style: 'blank' } as ContractSection);
    sections.push({
      type: 'header',
      id: genId(),
      content: '明细',
      align: 'left',
      fontSizePx: 14,
      bold: true,
    } as ContractSection);
    sections.push({
      type: 'free_table',
      id: genId(),
      colWidths: [40, 20, 20, 20],
      borderPx: 1,
      fontSizePx: null,
      rows: [
        {
          id: genId(),
          heightMm: null,
          cells: ['名称', '规格', '数量', '金额'].map((t) =>
            cell(t, { bold: true, shaded: true, align: 'center' }),
          ),
        },
        {
          id: genId(),
          heightMm: null,
          cells: Array.from({ length: cols }, () => cell('')),
        },
      ],
      // 列先不绑：子表字段要读对方表才知道，用户在属性面板里点两下就好，
      // 面板上有「每列取子表的哪个字段」的说明
      repeat: { source: 'link', linkFieldId: link.id, rowIndex: 1, columnFields: {} },
      showCount: true,
    } as unknown as ContractSection);
  }

  sections.push({ type: 'divider', id: genId(), style: 'blank' } as ContractSection);
  sections.push({
    type: 'signature',
    id: genId(),
    parties: ['甲方（盖章）', '乙方（盖章）'].map((label) => ({
      id: genId(),
      label,
      rows: [
        { id: genId(), label: '签字', fieldId: null, staticValue: '' },
        { id: genId(), label: '日期', fieldId: null, staticValue: '' },
      ],
    })),
  } as ContractSection);

  return sections;
}
